import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Users, Loader2, Crown, Search, Check, X, ChevronLeft, ChevronRight, Gift, ShieldCheck, ShieldOff, Trash2, AlertTriangle, Mail } from "lucide-react";
import { api, formatErr } from "../../lib/api";

/**
 * Admin-only card listing signups newest-first with rollups, search, paging,
 * and a per-row "Grant free Pro" dropdown (Lifetime / 3-months / 1-month /
 * Custom). Wraps `GET /admin/users`, `POST /admin/users/{id}/grant`, and
 * `POST /admin/users/{id}/revoke`.
 */
const PAGE_SIZE = 25;

const GRANT_OPTIONS = [
  { id: "lifetime", label: "Lifetime", desc: "Forever (gift to a friend or VIP camper)" },
  { id: "3months", label: "3 months", desc: "Long thank-you / influencer gift" },
  { id: "1month", label: "1 month", desc: "Goodwill / refund replacement" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

/** Format the signup attribution slug into a human-friendly label. */
const fmtSource = (src) => {
  if (!src) return "Direct";
  if (src.startsWith("ref:")) {
    const slug = src.slice(4);
    // Friendly labels for the slugs we set ourselves; fall back to the slug.
    const map = {
      "final-call-reel": "Final Call reel",
      "launch-push-reel": "Launch Push reels",
      "fnf-wa": "Friends/Family · WhatsApp",
      "fnf-fb": "Friends/Family · Messenger",
      "fnf-copy": "Friends/Family · Copy/Paste",
    };
    return map[slug] || slug;
  }
  if (src === "ig-organic") return "Instagram";
  if (src === "fb-organic") return "Facebook";
  if (src === "wa-organic") return "WhatsApp";
  if (src === "tiktok-organic") return "TikTok";
  if (src === "youtube") return "YouTube";
  if (src === "google-search") return "Google";
  if (src === "bing-search") return "Bing";
  if (src === "ddg-search") return "DuckDuckGo";
  if (src === "reddit") return "Reddit";
  if (src === "direct") return "Direct";
  return src;
};

export default function UserManagementCard() {
  const [data, setData] = useState({ users: [], total: 0, rollup: null });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [openMenu, setOpenMenu] = useState(null); // user_id with dropdown open
  const [customDays, setCustomDays] = useState({}); // user_id -> string
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [wipePreview, setWipePreview] = useState(null); // null | {candidates, content_counts}
  const [wiping, setWiping] = useState(false);
  // iter101 — bulk activation-email state
  const [activPreview, setActivPreview] = useState(null); // {eligible, preview[]}
  const [activSending, setActivSending] = useState(false);

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const skip = (opts.page ?? page) * PAGE_SIZE;
      const q = (opts.search ?? search).trim();
      const { data } = await api.get(`/admin/users`, {
        params: { skip, limit: PAGE_SIZE, search: q },
      });
      setData(data || { users: [], total: 0, rollup: null });
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setPage(0);
    load({ page: 0, search });
  };

  const grant = async (userId, duration, days) => {
    setBusyId(userId);
    setOpenMenu(null);
    try {
      const body = duration === "custom"
        ? { duration: "custom", custom_days: Number(days), notify: notifyEmail }
        : { duration, notify: notifyEmail };
      await api.post(`/admin/users/${userId}/grant`, body);
      const lbl = duration === "custom" ? `${days} day(s)` : duration;
      const mailNote = notifyEmail ? " · 📧 thank-you email sent" : "";
      toast.success(`Granted ${lbl} of free Pro${mailNote}`);
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusyId(null);
    }
  };

  const previewWipe = async () => {
    setWiping(true);
    try {
      const { data } = await api.get(`/admin/users/wipe-test/preview`);
      setWipePreview(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setWiping(false);
    }
  };

  const confirmWipe = async () => {
    setWiping(true);
    try {
      const { data } = await api.post(`/admin/users/wipe-test`, { confirm: "WIPE TEST ACCOUNTS" });
      toast.success(`Wiped ${data.deleted_users} test account(s)`);
      setWipePreview(null);
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setWiping(false);
    }
  };

  const revoke = async (userId, email) => {
    if (!window.confirm(`Revoke free Pro grant for ${email}? Paid Stripe subs are not affected.`)) return;
    setBusyId(userId);
    try {
      await api.post(`/admin/users/${userId}/revoke`);
      toast.success("Grant revoked");
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusyId(null);
    }
  };

  // iter114 — promote / demote admin role
  const setRole = async (userId, email, newRole) => {
    const verb = newRole === "admin" ? "Make admin" : "Revoke admin";
    const detail = newRole === "admin"
      ? `Grant FULL ADMIN access to ${email}? They will see the entire /admin dashboard, all users, payments and content moderation tools.`
      : `Revoke admin access from ${email}? They will go back to being a normal user.`;
    if (!window.confirm(`${verb}: ${email}?\n\n${detail}`)) return;
    setBusyId(userId);
    setOpenMenu(null);
    try {
      await api.post(`/admin/users/${userId}/set-role`, { role: newRole });
      toast.success(newRole === "admin" ? `${email} is now an admin 🛡` : `${email} is no longer an admin`);
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusyId(null);
    }
  };

  // iter121 — single-user delete + bulk wipe-non-admin
  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Delete account ${email}?\n\nThis cascade-deletes EVERY post, reply, story, favourite, chat, review and notification this user ever created. This cannot be undone.`)) return;
    setBusyId(userId);
    try {
      const { data } = await api.delete(`/admin/users/${userId}`);
      toast.success(`Deleted ${data.email || email}`);
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusyId(null);
    }
  };

  const [wipeNonAdminPreview, setWipeNonAdminPreview] = useState(null);
  const [wipingNonAdmin, setWipingNonAdmin] = useState(false);

  const previewWipeNonAdmin = async () => {
    setWipingNonAdmin(true);
    try {
      const { data } = await api.get(`/admin/users/wipe-non-admin/preview`);
      setWipeNonAdminPreview(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setWipingNonAdmin(false);
    }
  };

  const confirmWipeNonAdmin = async () => {
    setWipingNonAdmin(true);
    try {
      const { data } = await api.post(`/admin/users/wipe-non-admin`, { confirm: "WIPE NON-ADMIN USERS" });
      toast.success(`Wiped ${data.deleted_users} non-admin account(s)`);
      setWipeNonAdminPreview(null);
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setWipingNonAdmin(false);
    }
  };

  // iter101 — Trial-activation bulk email (existing free users w/ no card)
  const previewActivation = async () => {
    setActivSending(true);
    try {
      const { data } = await api.post(`/admin/email/send-trial-activation`, { dry_run: true });
      setActivPreview(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setActivSending(false);
    }
  };

  const confirmActivation = async () => {
    setActivSending(true);
    try {
      const { data } = await api.post(`/admin/email/send-trial-activation`, { dry_run: false });
      const failed = data.results?.filter((r) => r.status === "failed") || [];
      if (failed.length === 0) {
        toast.success(`Sent ${data.sent}/${data.total} activation email(s)`);
      } else {
        // iter102 — surface the actual Resend error so admin can self-diagnose
        // (e.g. domain not verified, API key mismatch, rate limit).
        const sample = failed.find((r) => r.error)?.error || "(no error message returned)";
        toast.error(
          `Sent ${data.sent}/${data.total} · ${failed.length} failed.\nResend says: ${sample.slice(0, 200)}`,
          { duration: 12000 }
        );
        // Also log the full list to the console for deeper inspection
        // eslint-disable-next-line no-console
        console.warn("[Activation blast] Per-recipient failures:", failed);
      }
      setActivPreview(null);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setActivSending(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const rollup = data.rollup || {};

  return (
    <div className="mb-10 p-5 border hairline bg-white" data-testid="user-management-card">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <div className="eyebrow mb-1 flex items-center gap-2">
            <Users className="w-3 h-3" /> User Management
          </div>
          <div className="font-display text-xl mb-1">Signups & free Pro grants</div>
          <p className="text-sm text-[#555] max-w-2xl">
            Newest-first. Search by email or display name. Grant a free Pro membership
            (Lifetime / 3-months / 1-month / Custom). Grants extend from the LATER of today
            or the current expiry, so you can never accidentally shorten a longer one.
          </p>
        </div>
      </div>

      {/* Rollup tiles */}
      {data.rollup && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border hairline bg-[#fafafa] mb-5" data-testid="user-rollup">
          <RollupTile label="Total signups" value={rollup.all_signups ?? 0} />
          <RollupTile label="Paid (Stripe)" value={rollup.paid_active ?? 0} border />
          <RollupTile label="Free grants" value={rollup.free_grants ?? 0} border />
          <RollupTile label="Last 7 days" value={rollup.last_7_days ?? 0} border accent />
        </div>
      )}

      {/* Notify toggle + Wipe test button */}
      <div className="mb-3 flex items-center justify-between gap-3 flex-wrap text-xs">
        <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="notify-email-toggle">
          <input
            type="checkbox"
            checked={notifyEmail}
            onChange={(e) => setNotifyEmail(e.target.checked)}
            className="accent-[#ff4500]"
          />
          <span className="text-[#333]">
            📧 Send thank-you + share-prompt email when granting (Resend)
          </span>
        </label>
        <button
          type="button"
          onClick={previewWipe}
          disabled={wiping}
          className="inline-flex items-center gap-1.5 border hairline px-2.5 py-1.5 text-[11px] text-[#b00020] hover:bg-[#fff0f0] disabled:opacity-50"
          data-testid="wipe-test-btn"
          title="Cascade-delete every account whose email starts with 'test_'"
        >
          {wiping ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
          Wipe test accounts
        </button>
        {/* iter121 — Bulk wipe-non-admin */}
        <button
          type="button"
          onClick={previewWipeNonAdmin}
          disabled={wipingNonAdmin}
          className="inline-flex items-center gap-1.5 border-2 border-[#b00020] px-2.5 py-1.5 text-[11px] text-white bg-[#b00020] hover:bg-[#8a0017] disabled:opacity-50 font-bold uppercase tracking-wider"
          data-testid="wipe-non-admin-btn"
          title="Cascade-delete EVERY non-admin account. Keeps you (and any other admins) intact."
        >
          {wipingNonAdmin ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
          Remove all non-admin users
        </button>
      </div>

      {/* iter121 — confirmation panel for the wipe-non-admin action */}
      {wipeNonAdminPreview && (
        <div className="mb-4 border-2 border-[#b00020] bg-[#fff0f0] p-4" data-testid="wipe-non-admin-panel">
          <div className="flex items-start gap-3 mb-3">
            <Trash2 className="w-5 h-5 text-[#b00020] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-display text-base mb-1">Confirm full non-admin wipe — this can't be undone</div>
              <p className="text-xs text-[#555]">
                Will delete <strong>{wipeNonAdminPreview.candidate_count}</strong> non-admin account(s) and cascade-delete every post / reply / story / favourite / chat / notification they ever created. <strong>All admin accounts are preserved.</strong>
              </p>
            </div>
          </div>
          {wipeNonAdminPreview.candidates.length > 0 && (
            <div className="max-h-44 overflow-y-auto mono text-[11px] text-[#444] mb-3 border hairline bg-white p-2">
              {wipeNonAdminPreview.candidates.map((c) => (
                <div key={c.user_id} className="py-0.5 border-b border-[#eee] last:border-0">
                  {c.email} {c.display_name ? `· ${c.display_name}` : ""}
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={confirmWipeNonAdmin}
              disabled={wipingNonAdmin || wipeNonAdminPreview.candidate_count === 0}
              className="inline-flex items-center gap-1.5 bg-[#b00020] text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-[#8a0017] disabled:opacity-50"
              data-testid="wipe-non-admin-confirm-btn"
            >
              {wipingNonAdmin ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
              Yes, delete {wipeNonAdminPreview.candidate_count} account(s)
            </button>
            <button
              type="button"
              onClick={() => setWipeNonAdminPreview(null)}
              className="inline-flex items-center border hairline px-3 py-1.5 text-xs hover:bg-[#f5f5f5]"
              data-testid="wipe-non-admin-cancel-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* iter101 — Trial activation email blast for existing free users */}
      <div className="mb-3 flex items-center justify-between gap-3 flex-wrap text-xs border hairline bg-[#fffaf5] p-3">
        <div className="min-w-0">
          <div className="font-display text-sm flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5 text-[#ff4500]" />
            Trial activation blast
          </div>
          <div className="text-[#555] mt-0.5 leading-snug">
            Emails every existing free user (no Stripe card, no lifetime grant) a one-tap link to start their 14-day free Pro trial.
            Idempotent — users are only emailed once.
          </div>
        </div>
        <button
          type="button"
          onClick={previewActivation}
          disabled={activSending}
          className="inline-flex items-center gap-1.5 border hairline px-2.5 py-1.5 text-[11px] text-[#ff4500] bg-white hover:bg-[#fff0e7] disabled:opacity-50 shrink-0"
          data-testid="activate-trial-preview-btn"
        >
          {activSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
          Preview & send
        </button>
      </div>

      {/* iter101 — confirmation panel for the activation blast */}
      {activPreview && (
        <div className="mb-4 border hairline bg-[#fffaf5] p-4" data-testid="activate-trial-panel">
          <div className="flex items-start gap-3 mb-3">
            <Mail className="w-5 h-5 text-[#ff4500] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-display text-base mb-1">Send activation emails to {activPreview.eligible} user(s)?</div>
              <p className="text-xs text-[#555]">
                Each gets a one-tap link to Stripe Checkout where they enter card details, get 14 free days, then £1.99/mo auto-charges on day 15 unless they cancel.
              </p>
            </div>
          </div>
          {activPreview.preview && activPreview.preview.length > 0 && (
            <div className="mb-3 max-h-32 overflow-y-auto border hairline bg-white p-2 text-[11px] mono">
              {activPreview.preview.map((r, i) => (
                <div key={i} className="text-[#555] truncate">{r.email}{r.name ? ` · ${r.name}` : ""}</div>
              ))}
              {activPreview.eligible > activPreview.preview.length && (
                <div className="text-[#888] italic mt-1">…and {activPreview.eligible - activPreview.preview.length} more</div>
              )}
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={confirmActivation}
              disabled={activSending || activPreview.eligible === 0}
              className="inline-flex items-center gap-1.5 bg-[#ff4500] text-white px-3 py-1.5 text-[11px] uppercase tracking-wide font-bold disabled:opacity-50"
              data-testid="activate-trial-confirm-btn"
            >
              {activSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
              Yes, email {activPreview.eligible} user(s)
            </button>
            <button
              onClick={() => setActivPreview(null)}
              disabled={activSending}
              className="btn-secondary !py-1.5 !px-3 text-[11px]"
              data-testid="activate-trial-cancel-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Wipe-test confirmation panel */}
      {wipePreview && (
        <div className="mb-4 border hairline bg-[#fff8f5] p-4" data-testid="wipe-test-panel">
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle className="w-5 h-5 text-[#b00020] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-display text-base mb-1">Confirm wipe — this can't be undone</div>
              <p className="text-xs text-[#555]">
                Will delete <strong>{wipePreview.candidate_count}</strong> test account(s)
                and cascade-delete every post / reply / story / favourite / chat / notification they ever created.
                The seeded <code className="mono">test@campcompass.uk</code> and all admin accounts are protected.
              </p>
            </div>
          </div>
          {wipePreview.candidates.length > 0 && (
            <div className="mb-3 max-h-32 overflow-y-auto border hairline bg-white p-2 text-[11px] mono">
              {wipePreview.candidates.map((c) => (
                <div key={c.user_id} className="text-[#555] truncate">{c.email}</div>
              ))}
            </div>
          )}
          <div className="mb-3 grid grid-cols-2 md:grid-cols-4 gap-1.5 text-[11px]">
            {Object.entries(wipePreview.content_counts).filter(([, v]) => v > 0).map(([k, v]) => (
              <div key={k} className="border hairline bg-white px-2 py-1.5">
                <div className="text-[10px] uppercase text-[#888] mono">{k}</div>
                <div className="font-display text-sm">{v}</div>
              </div>
            ))}
            {Object.values(wipePreview.content_counts).every((v) => !v) && (
              <div className="text-[11px] text-[#888] col-span-full">No related content to cascade — accounts only.</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={confirmWipe}
              disabled={wiping || wipePreview.candidate_count === 0}
              className="inline-flex items-center gap-1.5 bg-[#b00020] text-white px-3 py-1.5 text-[11px] uppercase tracking-wide font-bold disabled:opacity-50"
              data-testid="confirm-wipe-btn"
            >
              {wiping ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
              Yes, wipe {wipePreview.candidate_count} account(s)
            </button>
            <button
              onClick={() => setWipePreview(null)}
              disabled={wiping}
              className="btn-secondary !py-1.5 !px-3 text-[11px]"
              data-testid="cancel-wipe-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <form onSubmit={onSearchSubmit} className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#888]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email or display name…"
            className="w-full pl-9 pr-3 py-2 border hairline text-sm focus:outline-none focus:border-[#ff4500]"
            data-testid="user-search-input"
          />
        </div>
        <button type="submit" className="btn-secondary !py-2 !px-4 text-xs" data-testid="user-search-btn">
          Search
        </button>
        {search && (
          <button
            type="button"
            onClick={() => { setSearch(""); setPage(0); load({ page: 0, search: "" }); }}
            className="text-xs text-[#888] hover:text-[#ff4500]"
            data-testid="user-search-clear"
          >
            Clear
          </button>
        )}
      </form>

      {loading ? (
        <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : data.users.length === 0 ? (
        <div className="p-10 border hairline bg-[#fafafa] text-center text-sm text-[#888]" data-testid="user-list-empty">
          {search ? "No users match that search." : "No signups yet."}
        </div>
      ) : (
        <>
          <div className="border hairline overflow-x-auto" data-testid="user-list">
            <table className="w-full text-sm">
              <thead className="bg-[#fafafa] border-b hairline">
                <tr className="eyebrow">
                  <th className="text-left px-3 py-2.5">User</th>
                  <th className="text-left px-3 py-2.5" title="When they signed up.">Joined</th>
                  <th className="text-left px-3 py-2.5" title="Where they signed up from (ref slug, UTM, or referrer host).">Source</th>
                  <th className="text-left px-3 py-2.5">Status</th>
                  <th className="text-left px-3 py-2.5" title="If on a free Pro grant or Stripe trial, the date paid Pro charging begins. Empty if free user / no trial.">Pro starts paying</th>
                  <th className="text-right px-3 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* iter122 — Show signed-up regular users first, push admins (CampCompass
                    staff accounts) to the bottom. Backend returns newest-first;
                    we use a stable sort here so within each group the original order survives. */}
                {[...data.users]
                  .sort((a, b) => (a.is_admin ? 1 : 0) - (b.is_admin ? 1 : 0))
                  .map((u, idx) => {
                  const grantInfo = u.free_pro_grant;
                  const menuOpen = openMenu === u.user_id;
                  const isBusy = busyId === u.user_id;
                  return (
                    <tr key={u.user_id} className="border-b hairline last:border-b-0 hover:bg-[#fafafa]" data-testid={`user-row-${idx}`}>
                      <td className="px-3 py-3 align-top">
                        <div className="font-medium truncate max-w-[260px]" title={u.email}>
                          {u.display_name || u.email?.split("@")[0]}
                          {u.is_admin && (
                            <span className="ml-2 inline-flex items-center gap-1 text-[10px] mono uppercase text-[#ff4500] border hairline px-1.5 py-0.5">
                              <ShieldCheck className="w-2.5 h-2.5" /> admin
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-[#888] mono truncate max-w-[260px]" title={u.email}>{u.email}</div>
                      </td>
                      <td className="px-3 py-3 align-top mono text-xs text-[#555]" data-testid={`user-joined-${idx}`}>
                        {fmtDate(u.created_at)}
                      </td>
                      <td className="px-3 py-3 align-top text-xs">
                        <span
                          className={`mono ${u.signup_source ? "text-[#111]" : "text-[#888]"}`}
                          title={u.signup_referrer || u.signup_source || "Direct visit (no ref/utm/referrer)"}
                          data-testid={`user-source-${idx}`}
                        >
                          {fmtSource(u.signup_source)}
                        </span>
                      </td>
                      <td className="px-3 py-3 align-top">
                        {u.is_pro ? (
                          <span className="inline-flex items-center gap-1 text-[11px] mono uppercase bg-[#ff4500] text-white px-2 py-0.5">
                            <Crown className="w-3 h-3" /> Pro
                          </span>
                        ) : (
                          <span className="text-[11px] mono uppercase text-[#888]">Free</span>
                        )}
                        {grantInfo && (
                          <div className="text-[10px] mono text-[#888] mt-1">
                            <Gift className="w-2.5 h-2.5 inline mr-0.5" /> {grantInfo.type}
                          </div>
                        )}
                        {u.stripe_status && (
                          <div className="text-[10px] mono text-[#555] mt-1">Stripe: {u.stripe_status}</div>
                        )}
                        {/* iter100/iter101 — Inline "Pro starts paying" indicator so it stays
                            visible on mobile where the dedicated column scrolls off-screen.
                            iter101 differentiates trialing (date = day-15 charge) vs lifetime grant. */}
                        {u.is_pro && (
                          <div className="text-[10px] mono mt-1" data-testid={`user-pay-from-inline-${idx}`}>
                            {u.stripe_status === "active" ? (
                              <span className="text-[#0a7f3f]">Already paying</span>
                            ) : u.stripe_status === "trialing" && u.membership_expires_at ? (
                              <span className="text-[#b45309]">Trial → 1st charge {fmtDate(u.membership_expires_at)}</span>
                            ) : grantInfo ? (
                              <span className="text-[#888]">Free Pro grant (no auto-charge)</span>
                            ) : u.membership_expires_at ? (
                              <span className="text-[#b45309]">Pays from {fmtDate(u.membership_expires_at)}</span>
                            ) : (
                              <span className="text-[#888]">No charge date</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 align-top mono text-xs text-[#555]" data-testid={`user-pay-from-${idx}`}>
                        {u.stripe_status === "active" ? (
                          <span className="text-[#0a7f3f]">Already paying</span>
                        ) : (
                          fmtDate(u.membership_expires_at)
                        )}
                      </td>
                      <td className="px-3 py-3 align-top text-right relative">
                        <div className="inline-flex items-center gap-1.5">
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => setOpenMenu(menuOpen ? null : u.user_id)}
                              disabled={isBusy}
                              className="btn-primary !px-3 !py-1.5 text-[11px] inline-flex items-center gap-1.5 disabled:opacity-50"
                              data-testid={`grant-btn-${idx}`}
                            >
                              {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Gift className="w-3 h-3" />}
                              Grant Pro
                            </button>
                            {/* iter115 — Mobile-safe: the menu is rendered
                                outside the table via a portal-like modal at
                                the bottom of the component, so it can't be
                                clipped by overflow-x-auto. The dropdown for
                                wide screens still appears here. */}
                            {menuOpen && (
                              <div
                                className="hidden sm:block absolute right-0 mt-1 w-64 bg-white border hairline shadow-lg z-20"
                                data-testid={`grant-menu-${idx}`}
                              >
                                {GRANT_OPTIONS.map((opt) => (
                                  <button
                                    key={opt.id}
                                    onClick={() => grant(u.user_id, opt.id)}
                                    className="w-full text-left px-3 py-2 hover:bg-[#fafafa] border-b hairline last:border-b-0"
                                    data-testid={`grant-opt-${opt.id}-${idx}`}
                                  >
                                    <div className="font-medium text-sm">{opt.label}</div>
                                    <div className="text-[11px] text-[#888]">{opt.desc}</div>
                                  </button>
                                ))}
                                <div className="px-3 py-2 border-t hairline bg-[#fafafa]">
                                  <div className="eyebrow mb-1.5">Custom days</div>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="1"
                                      max="36500"
                                      value={customDays[u.user_id] || ""}
                                      onChange={(e) => setCustomDays({ ...customDays, [u.user_id]: e.target.value })}
                                      placeholder="e.g. 14"
                                      className="w-20 border hairline px-2 py-1 text-xs mono focus:outline-none focus:border-[#ff4500]"
                                      data-testid={`grant-custom-input-${idx}`}
                                    />
                                    <button
                                      onClick={() => {
                                        const d = customDays[u.user_id];
                                        if (!d || Number(d) <= 0) { toast.error("Enter a positive day count"); return; }
                                        grant(u.user_id, "custom", d);
                                      }}
                                      className="btn-primary !px-2 !py-1 text-[10px] inline-flex items-center gap-1"
                                      data-testid={`grant-custom-btn-${idx}`}
                                    >
                                      <Check className="w-3 h-3" /> Apply
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setOpenMenu(null)}
                                      className="text-[10px] text-[#888] hover:text-[#ff4500] ml-auto"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                                {/* iter114 — promote to admin row */}
                                {!u.is_admin && (
                                  <button
                                    type="button"
                                    onClick={() => setRole(u.user_id, u.email, "admin")}
                                    className="w-full text-left px-3 py-2 hover:bg-[#fff4ee] border-t hairline flex items-center gap-2"
                                    data-testid={`make-admin-${idx}`}
                                  >
                                    <ShieldCheck className="w-3.5 h-3.5 text-[#ff4500]" />
                                    <div>
                                      <div className="font-medium text-sm text-[#ff4500]">Make admin</div>
                                      <div className="text-[11px] text-[#888]">Grants full /admin dashboard access</div>
                                    </div>
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          {grantInfo && (
                            <button
                              onClick={() => revoke(u.user_id, u.email)}
                              disabled={isBusy}
                              className="btn-secondary !px-2 !py-1.5 text-[11px] inline-flex items-center gap-1 disabled:opacity-50"
                              data-testid={`revoke-btn-${idx}`}
                              title="Revoke free grant (paid Stripe subs untouched)"
                            >
                              <X className="w-3 h-3" /> Revoke
                            </button>
                          )}
                          {u.is_admin && (
                            <button
                              onClick={() => setRole(u.user_id, u.email, "user")}
                              disabled={isBusy}
                              className="btn-secondary !px-2 !py-1.5 text-[11px] inline-flex items-center gap-1 disabled:opacity-50"
                              data-testid={`revoke-admin-${idx}`}
                              title="Revoke admin role (last admin cannot be demoted)"
                            >
                              <ShieldOff className="w-3 h-3" /> Revoke admin
                            </button>
                          )}
                          {/* iter121 — Delete user (non-admins only) */}
                          {!u.is_admin && (
                            <button
                              onClick={() => deleteUser(u.user_id, u.email)}
                              disabled={isBusy}
                              className="!px-2 !py-1.5 text-[11px] inline-flex items-center gap-1 border border-[#b00020] text-[#b00020] hover:bg-[#fff0f0] disabled:opacity-50"
                              data-testid={`delete-user-${idx}`}
                              title="Cascade-delete this account and every post they created"
                            >
                              <Trash2 className="w-3 h-3" /> Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-3 text-xs">
            <div className="text-[#888] mono">
              Page {page + 1} of {totalPages} · {data.total} user(s)
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0 || loading}
                className="btn-secondary !px-2 !py-1.5 text-[11px] disabled:opacity-30 inline-flex items-center gap-1"
                data-testid="users-prev-page"
              >
                <ChevronLeft className="w-3 h-3" /> Prev
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1 || loading}
                className="btn-secondary !px-2 !py-1.5 text-[11px] disabled:opacity-30 inline-flex items-center gap-1"
                data-testid="users-next-page"
              >
                Next <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        </>
      )}
      {/* iter115 — Mobile bottom-sheet for the user actions menu.
          Renders OUTSIDE the table's overflow-x-auto wrapper so it can
          never be clipped horizontally. Only shows on small screens
          (sm:hidden) — desktop continues to use the inline dropdown. */}
      {openMenu && (() => {
        const u = (data?.users || []).find((x) => x.user_id === openMenu);
        if (!u) return null;
        return (
          <div className="sm:hidden fixed inset-0 z-50 flex items-end justify-center" data-testid="user-actions-sheet">
            <div className="absolute inset-0 bg-black/50" onClick={() => setOpenMenu(null)} />
            <div className="relative w-full max-h-[85vh] overflow-y-auto bg-white border-t hairline" style={{ borderTopLeftRadius: 14, borderTopRightRadius: 14 }}>
              <div className="px-4 py-3 border-b hairline sticky top-0 bg-white">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-base truncate">{u.display_name || u.email}</div>
                    <div className="text-[11px] mono text-[#888] truncate">{u.email}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpenMenu(null)}
                    className="ml-2 p-2 -mr-2 text-[#888]"
                    data-testid="sheet-close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="px-3 py-3">
                <div className="eyebrow mb-2">Grant Pro</div>
                {GRANT_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => grant(u.user_id, opt.id)}
                    className="w-full text-left px-3 py-3 border hairline mb-1.5 hover:bg-[#fafafa]"
                    data-testid={`sheet-grant-${opt.id}`}
                  >
                    <div className="font-medium text-sm">{opt.label}</div>
                    <div className="text-[11px] text-[#888]">{opt.desc}</div>
                  </button>
                ))}

                <div className="border hairline p-3 mb-3 bg-[#fafafa]">
                  <div className="eyebrow mb-1.5">Custom days</div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      max="36500"
                      value={customDays[u.user_id] || ""}
                      onChange={(e) => setCustomDays({ ...customDays, [u.user_id]: e.target.value })}
                      placeholder="e.g. 14"
                      className="flex-1 border hairline px-2 py-2 text-sm mono focus:outline-none focus:border-[#ff4500]"
                      data-testid="sheet-custom-input"
                    />
                    <button
                      onClick={() => {
                        const d = customDays[u.user_id];
                        if (!d || Number(d) <= 0) { toast.error("Enter a positive day count"); return; }
                        grant(u.user_id, "custom", d);
                      }}
                      className="btn-primary !px-3 !py-2 text-xs inline-flex items-center gap-1"
                      data-testid="sheet-custom-apply"
                    >
                      <Check className="w-3 h-3" /> Apply
                    </button>
                  </div>
                </div>

                <div className="eyebrow mb-2">Admin role</div>
                {!u.is_admin ? (
                  <button
                    type="button"
                    onClick={() => setRole(u.user_id, u.email, "admin")}
                    className="w-full text-left px-3 py-3 border-2 border-[#ff4500] bg-[#fff4ee] flex items-center gap-3"
                    data-testid="sheet-make-admin"
                  >
                    <ShieldCheck className="w-5 h-5 text-[#ff4500] flex-shrink-0" />
                    <div>
                      <div className="font-medium text-sm text-[#ff4500]">Make admin 🛡</div>
                      <div className="text-[11px] text-[#888]">Grants full /admin dashboard access</div>
                    </div>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setRole(u.user_id, u.email, "user")}
                    className="w-full text-left px-3 py-3 border hairline flex items-center gap-3"
                    data-testid="sheet-revoke-admin"
                  >
                    <ShieldOff className="w-5 h-5 text-[#555] flex-shrink-0" />
                    <div>
                      <div className="font-medium text-sm">Revoke admin role</div>
                      <div className="text-[11px] text-[#888]">Last admin cannot be demoted</div>
                    </div>
                  </button>
                )}

                {u.free_pro_grant && (
                  <>
                    <div className="eyebrow mb-2 mt-4">Free Pro grant</div>
                    <button
                      type="button"
                      onClick={() => revoke(u.user_id, u.email)}
                      className="w-full text-left px-3 py-3 border hairline flex items-center gap-3"
                      data-testid="sheet-revoke-grant"
                    >
                      <X className="w-5 h-5 text-[#555] flex-shrink-0" />
                      <div>
                        <div className="font-medium text-sm">Revoke free Pro grant</div>
                        <div className="text-[11px] text-[#888]">Paid Stripe subs not affected</div>
                      </div>
                    </button>
                  </>
                )}

                <div className="h-4" />
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

const RollupTile = ({ label, value, border, accent }) => (
  <div className={`p-3.5 ${border ? "border-l hairline" : ""} ${accent ? "bg-[#ffefea]" : ""}`}>
    <div className="eyebrow">{label}</div>
    <div className="font-display text-2xl mono mt-1">{value}</div>
  </div>
);
